<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, watch, nextTick } from "vue";
import Tooltip from "~/components/Common/Tooltip.vue";

/**
 * Single-line text that ellipsis-truncates to its container and shows the full value in a
 * design-system Tooltip on hover or keyboard focus — but only when it is actually truncated, and
 * only then is it focusable, so text that fits adds no tab stop. Use it for table headers / cells
 * where a value (e.g. a long financing-mode name) may not fit its column.
 */
const props = withDefaults(
  defineProps<{
    text?: string | null;
    /** Classes for the text itself (font, colour, …). */
    textClass?: string;
    align?: "left" | "center" | "right";
    position?: string;
  }>(),
  { text: "", textClass: "", align: "left", position: "top-mid" },
);

const textEl = ref<HTMLElement | null>(null);
const isTruncated = ref(false);

const measure = () => {
  const el = textEl.value;
  isTruncated.value = !!el && el.scrollWidth > el.clientWidth + 1;
};

let observer: ResizeObserver | null = null;
onMounted(() => {
  measure();
  observer = new ResizeObserver(measure);
  if (textEl.value) observer.observe(textEl.value);
});
onBeforeUnmount(() => observer?.disconnect());
watch(
  () => props.text,
  () => nextTick(measure),
);
</script>

<template>
  <Tooltip class="w-full min-w-0" :position="position" :disabled="!isTruncated" append-to-body minified open-on-focus>
    <template #img>
      <!-- Inset focus ring: table cells clip their overflow, which would hide an outer one. -->
      <span
        ref="textEl"
        class="block w-full truncate focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary-blue-color"
        :class="[textClass, { 'text-right': align === 'right', 'text-center': align === 'center' }]"
        :tabindex="isTruncated ? 0 : undefined"
        >{{ text }}</span
      >
    </template>
    <template #content>
      <span class="body_regular whitespace-nowrap">{{ text }}</span>
    </template>
  </Tooltip>
</template>
